import {
    IBulkProps,
    IItem,
} from './types';

import React, {
    useEffect,
    useRef,
} from 'react';

export function useShiftDragSelection(
    bulk: IBulkProps,
    items: IItem[] | null
) {
    const isShiftDraggingRef = useRef(false);
    const lastSelectedIdRef = useRef<string | number | null>(null);

    useEffect(() => {
        const handleMouseUp = () => {
            isShiftDraggingRef.current = false;
        };

        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    const onRowMouseDown = (
        event: React.MouseEvent,
        id: string | number
    ) => {
        if (!bulk?.isActiveIdsSelecting) {
            return;
        }
        if (event.button !== 0 || !event.shiftKey) {
            return;
        }

        event.preventDefault();

        isShiftDraggingRef.current = true;

        if (lastSelectedIdRef.current !== null && items) {
            const lastSelectedIndex = items.findIndex(
                (item) => item.id === lastSelectedIdRef.current
            );
            const currentIndex = items.findIndex(
                (item) => item.id === id
            );

            if (lastSelectedIndex !== -1 && currentIndex !== -1) {
                const startIndex = Math.min(
                    lastSelectedIndex,
                    currentIndex
                );
                const endIndex = Math.max(
                    lastSelectedIndex,
                    currentIndex
                );

                const rangeIds = items
                    .slice(startIndex, endIndex + 1)
                    .map((item) => item.id);

                bulk.setSelectedIds((selectedIds) => {
                    return Array.from(new Set([
                        ...selectedIds,
                        ...rangeIds,
                    ]));
                });

                return;
            }
        }
        if (!bulk.selectedIds.includes(id)) {
            bulk.setSelectedIds((selectedIds) => [
                ...selectedIds,
                id,
            ]);
        }
    };

    const onRowMouseEnter = (id: string | number) => {
        if (!bulk?.isActiveIdsSelecting) {
            return;
        }
        if (!isShiftDraggingRef.current) {
            return;
        }

        bulk.setSelectedIds((selectedIds) => {
            if (selectedIds.includes(id)) {
                return selectedIds;
            }

            return [
                ...selectedIds,
                id,
            ];
        });
    };

    const onToggleId = (id: string | number) => {
        lastSelectedIdRef.current = id;
    };

    return {
        onRowMouseDown,
        onRowMouseEnter,
        onToggleId,
    };
}
